import {
  Button,
  Chip,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@heroui/react';
import { ColumnDef } from '@tanstack/react-table';
import { Edit, Hotel, MoreVertical, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useHeroToast } from '../../../../hooks';
import { Ad, AD_TYPE_LABELS, useDeleteAd } from '../../../../queries';
import { useHotelOptions } from '../../hooks';

const AssignedHotelsCell: React.FC<{ ad: Ad }> = ({ ad }) => {
  const { hotelNameById } = useHotelOptions();
  const hotels = ad.assigned_hotels;

  if (hotels.length === 0) {
    return <span className="text-gray-400">0</span>;
  }

  return (
    <Popover placement="bottom">
      <PopoverTrigger>
        <button
          type="button"
          className="flex items-center gap-2 cursor-pointer hover:opacity-70 transition-opacity"
        >
          <Hotel size={16} />
          <span className="text-sm">{hotels.length}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent>
        <div className="px-2 py-2 max-w-[280px] flex flex-wrap gap-1.5">
          {hotels.map((hotelId) => (
            <Chip key={hotelId} size="sm" variant="flat">
              {hotelNameById.get(hotelId) || hotelId}
            </Chip>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const ActionsCell: React.FC<{ ad: Ad }> = ({ ad }) => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useHeroToast();
  const { mutate: deleteAd, isPending } = useDeleteAd();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDelete = () => {
    deleteAd(
      { ids: [ad.id] },
      {
        onSuccess: () => {
          showSuccess(`Usunięto reklamę „${ad.name}"`);
          setShowDeleteConfirm(false);
        },
        onError: (error) => {
          showError(error.message || 'Nie udało się usunąć reklamy');
          setShowDeleteConfirm(false);
        },
      },
    );
  };

  return (
    <div className="flex items-center justify-end h-[40px]">
      <Dropdown placement="bottom-end">
        <DropdownTrigger>
          <Button
            isIconOnly
            size="sm"
            variant="light"
            isDisabled={isPending}
            aria-label="Otwórz menu akcji"
          >
            <MoreVertical size={16} />
          </Button>
        </DropdownTrigger>
        <DropdownMenu
          aria-label="Akcje reklamy"
          onAction={(key) => {
            if (key === 'edit') navigate(`/ads/${ad.id}/edit`);
            if (key === 'delete') setShowDeleteConfirm(true);
          }}
        >
          {[
            <DropdownItem key="edit" startContent={<Edit size={14} />}>
              Szczegóły
            </DropdownItem>,
            <DropdownItem
              key="delete"
              color="danger"
              className="text-danger"
              startContent={<Trash2 size={14} />}
            >
              Usuń
            </DropdownItem>,
          ]}
        </DropdownMenu>
      </Dropdown>

      <Modal
        isOpen={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        size="sm"
        isDismissable={!isPending}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">Usunąć reklamę?</ModalHeader>
              <ModalBody>
                <p className="text-sm text-gray-600">
                  Przestanie się wyświetlać w
                  {' '}
                  {ad.assigned_hotels.length}
                  {' '}
                  hotelach. Tej operacji nie da się cofnąć.
                </p>
              </ModalBody>
              <ModalFooter>
                <Button variant="light" onPress={onClose} isDisabled={isPending}>
                  Anuluj
                </Button>
                <Button color="danger" onPress={handleDelete} isLoading={isPending}>
                  Usuń
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
};

export const AdColumns: ColumnDef<Ad>[] = [
  {
    accessorKey: 'image',
    header: '',
    enableSorting: false,
    cell: ({ row }) => (
      <Image
        src={row.original.image}
        alt={row.original.name}
        className="w-[64px] h-[40px] object-cover"
        radius="sm"
      />
    ),
  },
  {
    accessorKey: 'name',
    header: 'Nazwa',
  },
  {
    accessorKey: 'type',
    header: 'Typ',
    cell: ({ row }) => AD_TYPE_LABELS[row.original.type] ?? row.original.type,
  },
  {
    accessorKey: 'order',
    header: 'Kolejność',
  },
  {
    accessorKey: 'url',
    header: 'Link',
    enableSorting: false,
    cell: ({ row }) => (row.original.url ? (
      <a
        href={row.original.url}
        target="_blank"
        rel="noreferrer"
        className="text-primary hover:underline truncate block max-w-[220px]"
      >
        {row.original.url}
      </a>
    ) : <span className="text-gray-400">—</span>),
  },
  {
    id: 'assigned_hotels',
    header: 'Hotele',
    enableSorting: false,
    cell: ({ row }) => <AssignedHotelsCell ad={row.original} />,
  },
  {
    id: 'actions',
    header: '',
    enableSorting: false,
    cell: ({ row }) => <ActionsCell ad={row.original} />,
  },
];
